import {Fragment, useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import {Menu, Transition} from "@headlessui/react";
import {createPortal} from 'react-dom';
import {autoUpdate, computePosition, hide, offset, shift} from "@floating-ui/dom";

import PencilIcon from "@heroicons/react/outline/PencilIcon";
import DuplicateIcon from "@heroicons/react/outline/DuplicateIcon";
import ArrowCircleUp from "@heroicons/react/outline/ArrowCircleUpIcon";
import ArrowCircleDown from "@heroicons/react/outline/ArrowCircleDownIcon";
import XCircleIcon from "@heroicons/react/outline/XCircleIcon";
import ChevronDownIcon from "@heroicons/react/outline/ChevronDownIcon";

import {WebsiteContext} from '../index';

import {supportsField} from "./index";
import {backgroundColour} from "../utils/block-utils";

import AnimationOnScroll from '../components/animate-on-scroll';
import InlineAddBlockButton from "@autocx/forms/src/builder/inline-add-block-button";
import {nanoid} from "nanoid";
import HiddenBlock from "./hidden-block";
import ClipboardCopyIcon from "@heroicons/react/outline/ClipboardCopyIcon";

const BLOCK_ACTIONS = [
    {name: 'Edit', icon: PencilIcon},
    {name: 'Move Up', icon: ArrowCircleUp},
    {name: 'Move Down', icon: ArrowCircleDown},
    {name: 'Duplicate', icon: DuplicateIcon},
    {name: 'Delete', icon: XCircleIcon, type: 'destructive'},
];

function openNavigationSettings(id) {
    document.dispatchEvent(new CustomEvent('cx:open-navigation-settings', {
        bubbles: true,
        cancelable: true,
        detail: {navigation: id}
    }));
}

function BlockActions({className, onAction = () => {}, style, block}) {
    const context = useContext(WebsiteContext);
    let actions = [...BLOCK_ACTIONS];

    if (!context.blockTemplatesLookup[block.id]) {
        const insertAt = actions.length - 1; // before "Delete"
        if (block.isTemplatable !== false) {
            actions.splice(insertAt, 0, {name: "Make Template", icon: DuplicateIcon});
        }
        if (block.isCopyable !== false) {
            actions.splice(insertAt + (block.isTemplatable !== false ? 1 : 0), 0, {name: "Copy", icon: ClipboardCopyIcon});
        }
    }

    if (block.isEditable === false) actions = actions.filter(a => a.name !== "Edit");
    if (block.isMovable === false) actions = actions.filter(a => !a.name.startsWith("Move"));
    if (block.isDuplicable === false) actions = actions.filter(a => a.name !== "Duplicate");
    if (block.isDeletable === false) actions = actions.filter(a => a.name !== "Delete");

    const buttonRef = useRef(null);
    const cleanupRef = useRef(null);
    const [itemsEl, setItemsEl] = useState(null);

    if (!actions.length) {
        if (!block.readOnlyLabel) return null;

        return (
            <div
                id={`block_actions_${block.id}`}
                style={{...style, pointerEvents: "none"}}
                className={classNames(
                    "opacity-0 transition-opacity ease-in absolute transform z-[1001] right-4 top-0 block-actions h-full",
                    className
                )}
            >
                <div className="inline-flex sticky py-4 top-0 pointer-events-auto">
                    <span className="bg-black/80 backdrop-blur-md shadow-lg text-white inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium">
                        {block.readOnlyLabel}
                    </span>
                </div>
            </div>
        );
    }

    const primaryAction = actions[0];
    const PrimaryIcon = primaryAction.icon;

    if (actions.length === 1) {
        return (
            <div
                id={`block_actions_${block.id}`}
                style={{...style, pointerEvents: "none"}}
                className={classNames(
                    "opacity-0 transition-opacity ease-in absolute transform z-[1001] right-4 top-0 block-actions h-full",
                    className
                )}
            >
                <div className="inline-flex sticky py-4 top-0 pointer-events-auto">
                    <button
                        type="button"
                        onClick={() => onAction(primaryAction, block)}
                        className="bg-black/90 backdrop-blur-md shadow-lg text-white relative inline-flex items-center px-4 py-2 border border-white rounded-full text-sm font-medium focus:outline-none"
                    >
                        <PrimaryIcon className="h-4 w-4 mr-1"/>
                        {primaryAction.name}
                    </button>
                </div>
            </div>
        );
    }

    const position = () => {
        const reference = buttonRef.current;
        const floating = itemsEl;
        if (!reference || !floating) return;

        computePosition(reference, floating, {
            placement: "bottom-end",
            strategy: "absolute", // anchor to the button, not the viewport
            middleware: [
                offset(6),
                shift({mainAxis: false, crossAxis: true, padding: 8}),
                hide(),
            ],
        }).then(({x, y}) => {
            Object.assign(floating.style, {left: `${x}px`, top: `${y}px`});
        });
    };

    return (
        <div
            id={`block_actions_${block.id}`}
            style={{...style, pointerEvents: "none"}}
            className={classNames(
                "opacity-0 transition-opacity ease-in absolute transform z-[1001] right-4 top-0 block-actions h-full",
                className
            )}
        >
            <Menu as="div" className="inline-flex sticky py-4 top-0 pointer-events-auto">
                {({open}) => {
                    useEffect(() => {
                        const blockRoot = buttonRef.current?.closest("[data-block]");
                        if (!blockRoot) return;
                        if (open) blockRoot.classList.add("cx-hover-lock");
                        else blockRoot.classList.remove("cx-hover-lock");
                        return () => blockRoot.classList.remove("cx-hover-lock");
                    }, [open]);

                    useEffect(() => {
                        if (!open || !itemsEl) return;
                        cleanupRef.current = autoUpdate(buttonRef.current, itemsEl, position);
                        position();
                        return () => cleanupRef.current?.();
                    }, [open, itemsEl]);

                    useEffect(() => {
                        if (!open) return;
                        const blockRoot = buttonRef.current?.closest("[data-block]");
                        if (!blockRoot) return;

                        const scrollEl = document.getElementById('centre_scroll_container') || null;

                        const isOutOfView = () => {
                            const elRect = blockRoot.getBoundingClientRect();
                            if (scrollEl) {
                                const cRect = scrollEl.getBoundingClientRect();
                                const below = elRect.top >= cRect.bottom;
                                const above = elRect.bottom <= cRect.top;
                                return above || below;
                            } else {
                                const vh = window.innerHeight;
                                const below = elRect.top >= vh;
                                const above = elRect.bottom <= 0;
                                return above || below;
                            }
                        };

                        const handleScrollOrResize = () => {
                            if (isOutOfView()) {
                                // Toggle the menu closed
                                buttonRef.current?.click();
                            }
                        };

                        const target = scrollEl ?? window;
                        target.addEventListener('scroll', handleScrollOrResize, { passive: true });
                        window.addEventListener('resize', handleScrollOrResize);

                        // run once immediately
                        handleScrollOrResize();

                        return () => {
                            target.removeEventListener('scroll', handleScrollOrResize);
                            window.removeEventListener('resize', handleScrollOrResize);
                        };
                    }, [open]);

                    return (
                        <>
                            <button
                                type="button"
                                onClick={() => onAction(primaryAction, block)}
                                className="bg-black/90 backdrop-blur-md shadow-lg text-white relative inline-flex items-center px-4 py-2 border border-white border-r-0 rounded-l-full text-sm font-medium focus:outline-none"
                            >
                                <PrimaryIcon className="h-4 w-4 mr-1"/>
                                {primaryAction.name}
                            </button>

                            <Menu.Button
                                ref={buttonRef}
                                className="bg-black/90 backdrop-blur-md shadow-lg text-white relative inline-flex items-center px-3 py-2 border border-white rounded-r-full text-sm font-medium focus:outline-none"
                            >
                                <ChevronDownIcon className="h-5 w-5 -ml-0.5" aria-hidden="true"/>
                            </Menu.Button>

                            {open &&
                                createPortal(
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items
                                            ref={setItemsEl}
                                            style={{maxHeight: '60vh', overflowY: 'auto'}} // keep long menus scrollable
                                            className={classNames(
                                                "absolute z-[3] origin-top-right w-44 p-1",
                                                "rounded-lg border border-white bg-black/90 text-white backdrop-blur-md shadow-xl",
                                                "focus:outline-none"
                                            )}
                                        >
                                            {actions.slice(1).map(({icon: Icon, ...item}) => (
                                                <Menu.Item key={item.name}>
                                                    {({active}) => (
                                                        <button
                                                            type="button"
                                                            onClick={() => onAction(item, block)}
                                                            className={classNames(
                                                                "flex w-full items-center justify-between rounded-md px-2 py-2 text-sm",
                                                                active ? "bg-black/60" : "",
                                                                item.type === "destructive" ? "text-red-500" : "text-current"
                                                            )}
                                                        >
                                                            {item.name}
                                                            <Icon className="h-4 w-4 ml-1"/>
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            ))}
                                        </Menu.Items>
                                    </Transition>,
                                    document.body
                                )}
                        </>
                    );
                }}
            </Menu>
        </div>
    );
}

function NavigationActions({className, onAction, style, ...props}) {
    const PrimaryIcon = BLOCK_ACTIONS[0].icon;

    return (
        <div id={`block_actions_navigation`} style={{...style, pointerEvents: "none"}}
             className={classNames(
                 "opacity-0 transition-opacity ease-in absolute transform z-[1001] right-4 top-0 block-actions h-full",
                 className,
             )}>
            <div className={"inline-flex sticky py-4 top-0 pointer-events-auto"}>
                <button
                    type="button"
                    onClick={() => {
                        openNavigationSettings(props.block.id)
                    }}
                    className="bg-black bg-opacity-70 backdrop-blur-md backdrop-filter shadow-lg text-white fill-current relative inline-flex border border-white items-center px-4 py-2 rounded-full text-sm font-medium focus:outline-none"
                >
                    <PrimaryIcon className="h-4 w-4 mr-1"/>
                    {BLOCK_ACTIONS[0].name}
                </button>

            </div>
        </div>
    )
}

Block.propTypes = {
    style: PropTypes.object,
    className: PropTypes.string,
    actions: PropTypes.bool,
    onAction: PropTypes.func,
    onSelect: PropTypes.func,
    selectedBlock: PropTypes.string,
}

Block.defaultProps = {
    actions: false
}

function BlockDivider({nextBlock, block}) {
    return (
        <div
            className={!nextBlock?.values?.options?.inset ? backgroundColour(nextBlock?.values?.options?.backgroundColour) + '-svg-fill' : null}
            dangerouslySetInnerHTML={{__html: block.values?.options?.blockDivider}}
            style={{overflow: "hidden"}}
        />
    )
}

function AnimationWrapper(props) {
    const [reduceMotion, setReduceMotion] = useState(false);
    const context = useContext(WebsiteContext);

    useEffect(() => {
        // Only runs in the browser
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduceMotion(mediaQuery.matches === true);
    }, []);

    // On the very first render (SSR + initial client), reduceMotion is false
    // Then a split second later, it re-renders on the client if needed.

    if (
        props.isVisible ||
        reduceMotion ||
        props.animationStyle === 0 ||
        props.animationStyle === undefined ||
        (!context.isDesign && context.builder) ||
        context.picker ||
        context.isPicker ||
        props.children.props.children.type === 'header'
    ) {
        return props.children;
    }

    /*
        Return the Block without the Animation wrapper if the user has reduce motion turned on in their system settings,
        if no animation setting is in their theme context or if we're looking at a page through the builder. 
        i.e. The wrapper should only appear if an animation style is chosen and we're in the Design tab or on the preview/live site.
    */
    return (
        <AnimationOnScroll
            animateIn={props.animationStyle}
            duration={props.duration}
            scrollableParentSelector={context.builder ? "#centre_scroll_container" : null}
            key={`${props.blockId}_${props.animationStyle}_${props.duration}`}
            animateOnce={true}
            className={"relative"}
        >
            {props.children}
        </AnimationOnScroll>
    )
}

function BuilderBlockContainer(props) {
    const context = useContext(WebsiteContext);
    const hideInlineAddBlockButtons = props.hideInlineAddBlockButton || context.isEditing;
    const hideInlineAddBlockButtonBefore = hideInlineAddBlockButtons || props.hideInlineAddBlockButtonBefore;
    const hideInlineAddBlockButtonAfter = hideInlineAddBlockButtons || props.hideInlineAddBlockButtonAfter;
    const handleOnClick = (index) => {
        index = index < 0 ? 0 : index;
        document.dispatchEvent(new CustomEvent('cx:on-component-add', {
            bubbles: true,
            cancelable: true,
            detail: {
                component: {
                    id: nanoid(),
                    type: 'placeholder-block',
                    placeholder: true,
                    values: {}
                },
                index
            }
        }));
    }
    /*
    Only show the builder block container when in the editor
    */
    if (!context.isDesign && !context.builder) {
        return props.children
    } else {
        return (
            <div className={"relative block-builder-container"}>
                {!hideInlineAddBlockButtonBefore ? (
                    <InlineAddBlockButton
                        key={`minus_${context.isEditing}`}
                        index={props.index}
                        className={"invisible absolute top-[-12px]"}
                        label={"Add Block"}
                        onClick={() => handleOnClick(props.index)}
                    />
                ) : null}
                {props.children}
                {!hideInlineAddBlockButtonAfter ? (
                    <InlineAddBlockButton
                        key={`plus_${context.isEditing}`}
                        className={"invisible absolute bottom-[-12px]"}
                        label={"Add Block"}
                        index={props.index + 1}
                        onClick={() => handleOnClick(props.index + 1)}

                    />
                ) : null}
            </div>
        )
    }
}

const hideOn = (value) => {
    switch (value) {
        case 'mobile':
            return 'hidden sm:block';
        case 'desktop':
            return 'sm:hidden block';
        case 'both':
            return 'hidden';
        default:
            return ''
    }
}

export default function Block({
                                  style,
                                  className,
                                  children,
                                  actions,
                                  onAction,
                                  onSelect,
                                  selectedBlock,
                                  index,
                                  isHiddenBlock,
                                  state,
                                  hideInlineAddBlockButton,
                                  hideInlineAddBlockButtonBefore,
                                  hideInlineAddBlockButtonAfter,
                                  as = 'section',
                                  ...block
                              }) {
    const context = useContext(WebsiteContext);
    const blockRef = useRef();
    const BlockComponent = as;
    const isBuilder = context.builder;
    const [duration, setDuration] = useState(0)
    // When passing the style prop, ensure it's an object:
    const safeStyle = typeof style === 'object' && style !== null ? style : {};

    if (!isBuilder && (block.hideOn === 'both' || state === 'hidden')) return null;

    useEffect(() => {
        if (blockRef.current) {
            Array.from(blockRef.current?.children)
                .forEach((child) => {
                    child.classList.toggle('select-none', context.picker === true);
                    child.classList.toggle('pointer-events-none', context.picker === true);
                });
        }
    }, [blockRef.current, context.picker]);

    useEffect(() => {
        const containerHeight = isBuilder ? document.getElementById('centre_scroll_container').offsetHeight : window.innerHeight;
        if (blockRef.current?.getBoundingClientRect().top > containerHeight) {
            setDuration(1);
        }
    }, []);

    if (isBuilder && !isHiddenBlock && (state === 'hidden' || block.hideOn === 'both')) {
        let emptyMessage;
        if (typeof block.emptyMessage === 'string' && block.emptyMessage?.trim()) {
            emptyMessage = block.emptyMessage;
        } else if (state === 'hidden') {
            emptyMessage = "Because there are no items assigned or available, this block will be hidden from visitors on the live website. You can still edit or update it here in the builder.";
        } else if (block.hideOn === 'both') {
            emptyMessage = "This block is hidden on both desktop and mobile, as configured in block settings.";
        }

        return (
            <HiddenBlock
                {...block}
                style={style}
                className={className}
                children={children}
                actions={actions}
                onAction={onAction}
                onSelect={onSelect}
                selectedBlock={selectedBlock}
                index={index}
                state={state}
                hideInlineAddBlockButton={hideInlineAddBlockButton}
                hideInlineAddBlockButtonBefore={hideInlineAddBlockButtonBefore}
                hideInlineAddBlockButtonAfter={hideInlineAddBlockButtonAfter}
                as={as}
                emptyMessage={emptyMessage}
            />
        );
    }

    return (
        <AnimationWrapper duration={duration}
                          blockId={block.id}
                          animationStyle={context.theme.selectedBlockAnimation}>
            <BuilderBlockContainer index={index}
                                   hideInlineAddBlockButton={!actions || hideInlineAddBlockButton}
                                   hideInlineAddBlockButtonBefore={hideInlineAddBlockButtonBefore}
                                   hideInlineAddBlockButtonAfter={hideInlineAddBlockButtonAfter}>
                <BlockComponent
                    id={`block_${block.id}`}
                    ref={blockRef}
                    style={safeStyle}
                    data-block
                    className={classNames(
                        "break-words",
                        block.values?.options?.inset ? 'inset-block' : null,
                        block.websiteContext?.rootPage?.nodeType === 'blog' ? 'sm:rounded-theme rounded-none' : null,
                        actions ? 'actions' : null,
                        block.values?.design === undefined || block.values?.design === null ? null : `${block.type}-${block.values?.design ?? 0}`,
                        onSelect ? "cursor-pointer" : null,
                        isBuilder ? 'z-0' : null,
                        selectedBlock === block.id ? "selected" : null,
                        block.classNames,
                        className,
                        !isBuilder && block.hideBlock ? hideOn(block.hideOn) : null,
                        block.customClass
                    )}
                    onClick={onSelect ? (e => onSelect(e, block)) : null}
                    onDoubleClick={() => {
                        if (isBuilder && block.isEditable !== false) {
                            if (block.id === "navigation" || block.id === "footer") {
                                openNavigationSettings(block.id)
                            } else {
                                onAction(BLOCK_ACTIONS[0], block);
                                let sel = window.getSelection();
                                sel.removeAllRanges();
                            }
                        }
                    }}>
                    {actions && (block.id === 'navigation' || block.id === 'footer') &&
                        <NavigationActions block={block}/>}
                    {actions && !(block.id === 'navigation' || block.id === 'footer') &&
                        <BlockActions block={block} onAction={onAction}/>}
                    {children}
                    {supportsField(block, 'options.blockDivider') &&
                        <BlockDivider block={block} nextBlock={block.nextBlock}/>
                    }
                </BlockComponent>
            </BuilderBlockContainer>
        </AnimationWrapper>
    )
}
